import { AgentInteractionStatus } from "@/common/constants/callSession";
import { CallSession } from "@/common/models/callSession";
import { ServerEvent } from "@/common/notification";
import {
	NavigationActions,
	NavigationState,
	useNavigationStore,
} from "@/common/stores/persistingStores/navigation";
import { notificationListener } from "@/hooks/notification";
import {
	InteractionDto,
	InteractionOperations,
	InteractionStatus,
} from "@/repository/models";
import {
	CacheKey,
	useAppCacheStore,
	AppCacheState,
	AppSettingsState,
} from "@/common/stores/persistingStores/app";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import {
	WrapUpState,
	WrapUpActions,
	useWrapUpStore,
} from "@/common/stores/nonpersistingStores/wrapup";
import { useEffect, useRef, useState } from "react";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { WrapUpEndType } from "@/common/models/wrapUp";
import { useInteractions } from "@/components/organisms/interaction/useInteractions";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";

export const useNavigationTabsManager = () => {
	const [hadWrapUp, setHadWrapUp] = useState<boolean>(false);
	const navigationStore = useNavigationStore(
		(state: NavigationState & NavigationActions) => state
	);

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const wrapUp = useWrapUpStore((state: WrapUpState) => state.wrapUp);
	const updateWrapUp = useWrapUpStore(
		(actions: WrapUpActions) => actions.update
	);

	const setSelectedTab = useNavigationStore(
		(state: NavigationActions) => state.setSelectedTab
	);

	const isAutoSwitchToLastTabEnabled = useAppSettingsStore(
		(state: AppSettingsState) => state.settings.autoSwitchingToLastTabEnabled
	);

	const [selectedId, setSelectedId] = useState<string>();
	const rejectedCallIds = useRef(new Set<string>());

	const { interactions, updateCurrentInteraction } = useInteractions(
		selectedId,
		setSelectedId,
		true
	);

	const videoCallSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	// Remembers the agent-initiated video call currently in progress (its id and the webchat it was
	// started from). The call surfaces as its own Call interaction, which nothing else selects: unlike
	// an offered call it is placed server-side, so no client action brings the agent to it. Select it
	// when it starts, and re-select the webchat it belongs to once it ends.
	const videoCallSourceRef = useRef<{
		callId: string;
		conversationId: string;
	}>();
	const selectedVideoCallIdRef = useRef<string>();

	useEffect(() => {
		// While the agent is on the video call, the store holds it as the current call session.
		if (
			videoCallSession?.webchatConversationId &&
			videoCallSession.id &&
			videoCallSession.state !== AgentInteractionStatus.Terminated
		) {
			videoCallSourceRef.current = {
				callId: videoCallSession.id,
				conversationId: videoCallSession.webchatConversationId,
			};

			// Select it once, so the agent keeps whatever they navigate to afterwards.
			if (selectedVideoCallIdRef.current === videoCallSession.id) {
				return;
			}

			// Its Call interaction may not have reached the list yet — the call session update races the
			// notification that adds it. This effect re-runs on `interactions`, so leave the marker unset
			// and pick it up on the next pass rather than losing the selection.
			const callInteraction = interactions?.active?.find(
				(x) => x.id === videoCallSession.id
			);

			if (callInteraction) {
				selectedVideoCallIdRef.current = videoCallSession.id;
				updateCurrentInteraction(undefined, callInteraction);
			}

			return;
		}

		const endedVideoCall = videoCallSourceRef.current;

		if (!endedVideoCall) {
			return;
		}

		videoCallSourceRef.current = undefined;
		selectedVideoCallIdRef.current = undefined;

		const sourceWebchat = interactions?.active?.find(
			(x) => x.id === endedVideoCall.conversationId
		);

		if (sourceWebchat) {
			updateCurrentInteraction(undefined, sourceWebchat);
		}
	}, [videoCallSession, interactions]);
	
	const endWrapUpTypes = [
		WrapUpEndType.ByCallPickUp,
		WrapUpEndType.ByOffering,
	] as WrapUpEndType[];

	useEffect(() => {
		if (wrapUp) {
			setHadWrapUp(
				!wrapUp.endWrapUpType || !endWrapUpTypes.includes(wrapUp.endWrapUpType)
			);
		} else {
			if (hadWrapUp) {
				const previousTab = appStoreCache[CacheKey.PreviousTab];

				if (previousTab && isAutoSwitchToLastTabEnabled) {
					setSelectedTab(previousTab);
				}
			}

			setHadWrapUp(false);
		}
	}, [wrapUp, hadWrapUp, appStoreCache]);

	useEffect(() => {
		if (
			!hadWrapUp &&
			wrapUp &&
			wrapUp.endWrapUpType &&
			endWrapUpTypes.includes(wrapUp.endWrapUpType)
		) {
			updateWrapUp(null);
		}
	}, [hadWrapUp, wrapUp]);

	const consumeIfRejected = (id: string | undefined): boolean => {
		if (!id || !rejectedCallIds.current.has(id)) {
			return false;
		}

		rejectedCallIds.current.delete(id);

		return true;
	};

	const changePreviousTabIfNeeded = (interaction: InteractionDto) => {
		const previousTab = appStoreCache[CacheKey.PreviousTab];

		if (
			interaction.changeType === InteractionOperations.Removed &&
			interaction.status !== InteractionStatus.Waiting &&
			interaction.status !== InteractionStatus.ColdTransferRinging &&
			(interaction.status !== InteractionStatus.Parked || !interaction.isHandledByAiAgent) &&
			navigationStore.activeInteractionsCount <= 1 &&
			previousTab &&
			isAutoSwitchToLastTabEnabled
		) {
			setSelectedTab(previousTab);
		}
	};

	notificationListener<InteractionDto>(
		ServerEvent.ActiveMessagingChanged,
		(interaction: InteractionDto) => {
			changePreviousTabIfNeeded(interaction);
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.ActiveCallChanged,
		(interaction: InteractionDto) => {
			if (
				interaction.changeType == InteractionOperations.Removed &&
				wrapUp &&
				wrapUp.mediaId == interaction.id
			) {
				return;
			}

			if (
				interaction.changeType === InteractionOperations.Removed &&
				consumeIfRejected(interaction.id)
			) {
				return;
			}

			changePreviousTabIfNeeded(interaction);
		}
	);

	notificationListener<InteractionDto>(
		ServerEvent.SupervisedActiveInteractionChanged,
		(interaction: InteractionDto) => {
			const userId = Lockr.get(StorageKeys.UserId);

			if (interaction.monitoringSupervisorId !== userId) {
				return;
			}

			if (interaction.status === InteractionStatus.Parked) {
				return;
			}

			changePreviousTabIfNeeded(interaction);
		}
	);

	const isLastEndedInteraction = (
		callSession: CallSession,
		isMonitoring: boolean
	) => {
		if (navigationStore.activeInteractionsCount === 0) {
			return true;
		}

		const callSessionInteraction = interactions?.active?.find(
			(x) => x.id === callSession.id
		);
		if (
			navigationStore.activeInteractionsCount === 1 &&
			isMonitoring &&
			callSessionInteraction
		) {
			return true;
		}

		return false;
	};

	notificationListener(
		ServerEvent.CallSessionStateChanged,
		(callSession: CallSession) => {
			if (callSession?.state === AgentInteractionStatus.Rejected && callSession.id) {
				rejectedCallIds.current.add(callSession.id);
			}

			const agentId = Lockr.get(StorageKeys.UserId);
			
			const isSecondaryAgentWarmCancel =
				callSession?.state === AgentInteractionStatus.Active &&
				callSession.lastWarmCancelDate !== "" &&
				callSession.secondaryAgentId == agentId;

			if (
				callSession?.state === AgentInteractionStatus.Terminated ||
				callSession?.state === AgentInteractionStatus.MonitoringEnded ||
				isSecondaryAgentWarmCancel
			) {
				if (!consumeIfRejected(callSession.id)) {
					const isMonitoring = callSession.supervisorId === agentId;

					const previousTab = appStoreCache[CacheKey.PreviousTab];
					if (
						!callSession.activeWrapUpAgentsId?.includes(agentId) &&
						!callSession.isCallIgnored &&
						!callSession.isCallerClosedBeforeAnswer &&
						(callSession.isAgentEnableWrapupCheckDone ||
							isMonitoring ||
							isSecondaryAgentWarmCancel) &&
						previousTab &&
						isLastEndedInteraction(callSession, isMonitoring) &&
						isAutoSwitchToLastTabEnabled
					) {
						setSelectedTab(previousTab);
					}
				}

				// The tab count is not adjusted here: it follows the active interactions list.
				// useInteractions drops an agent's call on ActiveCallChanged Removed or, when the agent
				// has wrap-up, on WrapUpStopped; a supervisor's monitored call leaves the list through
				// the interactions refetch that callSessionManager triggers on MonitoringEnded. A call in
				// wrap-up stays in the list until wrap-up ends, so it still counts.
			}
		}
	);
};
